// 🔊 EMULATOR OVOZI (2026-11-01, backend docs/knowledge/204): ilova emulatorda chiqargan ovozni shu yerda eshittirish.
// Server `emulator:audio` bilan 24 kHz mono 16-bit PCM bo'laklarini (~60 ms) yuboradi — bu yerda WebAudio navbati
// bilan ketma-ket ijro etiladi (kechikish ~0.15 s). Brauzer ovozni faqat foydalanuvchi bosgandan keyin chalishga
// ruxsat beradi — shuning uchun tugma boshida o'chiq va AudioContext bosish paytida yaratiladi.
// Bitta hook = bitta socket (App.js, /m, /emu-view har biri o'zinikini ishlatadi); bir nechta slot birga eshitiladi.
import { useEffect, useRef, useState } from 'react';

const RATE = 24000;
const LEAD = 0.12;   // navbat boshida zaxira (tarmoq sakrashlariga)
const MAX_AHEAD = 0.6; // shundan ko'p yig'ilib qolsa (fon tabi, sekin tarmoq) — bo'laklar tashlanadi, jonli nuqtaga yetadi
// AGC: Android ushlagan ovoz manbadan ~26 dB past keladi (backend 204 o'lchovi) — cho'qqini ~−6 dBFS ga tortamiz.
// Kuchaytirish tez pasayadi (baland tovush), sekin ko'tariladi (jimlikdan keyin keskin «portlash» bo'lmasin).
const AGC_TARGET = 0.5, AGC_MAX = 24, AGC_DECAY = 0.97, AGC_RISE = 0.08;

export function useEmuAudio(socketRef, { asUser, notify } = {}) {
  const [audio, setAudio] = useState({}); // slot -> 'starting' | 'on'
  const r = useRef({ ctx: null, out: null, at: {}, on: {}, sock: null, agc: {} });
  const asUserRef = useRef(asUser);
  asUserRef.current = asUser;
  const notifyRef = useRef(notify);
  notifyRef.current = notify;

  const set = (slot, v) => setAudio(a => { const n = { ...a }; if (v) n[slot] = v; else delete n[slot]; return n; });
  const mine = (p) => p && (asUserRef.current == null || Number(p.userId) === Number(asUserRef.current));

  const play = (slot, data) => {
    const { ctx, out } = r.current;
    if (!ctx || !out || !data) return;
    if ((r.current.at[slot] || 0) > ctx.currentTime + MAX_AHEAD) return; // ustma-ust chalmaymiz — navbat bo'shaguncha tashlaymiz
    const u8 = data instanceof ArrayBuffer ? new Uint8Array(data) : (data.buffer ? new Uint8Array(data.buffer, data.byteOffset || 0, data.byteLength) : null);
    if (!u8 || u8.length < 2) return;
    const n = u8.length >> 1;
    const dv = new DataView(u8.buffer, u8.byteOffset, n * 2);
    const buf = ctx.createBuffer(1, n, RATE);
    const ch = buf.getChannelData(0);
    let peak = 0;
    for (let i = 0; i < n; i++) { const v = dv.getInt16(i * 2, true) / 32768; ch[i] = v; const a = v < 0 ? -v : v; if (a > peak) peak = a; }
    const g = r.current.agc[slot] || (r.current.agc[slot] = { lvl: 0, gain: AGC_MAX });
    g.lvl = Math.max(peak, g.lvl * AGC_DECAY);
    const want = Math.min(AGC_MAX, AGC_TARGET / Math.max(g.lvl, 1e-4));
    const g0 = g.gain, g1 = want < g0 ? want : g0 + (want - g0) * AGC_RISE;
    g.gain = g1;
    // Pasayish shu bo'lakning o'ziga darhol (cho'qqisini bilamiz — qirqilmaydi), ko'tarilish bo'lak ichida silliq (chirsillamasin).
    for (let i = 0; i < n; i++) ch[i] *= g1 < g0 ? g1 : g0 + (g1 - g0) * (i / n);
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.connect(out);
    const now = ctx.currentTime;
    let at = r.current.at[slot] || 0;
    if (at < now + 0.02) at = now + LEAD;      // navbat bo'shab qolgan (uzilish) — zaxira bilan qayta boshlash
    src.start(at);
    r.current.at[slot] = at + buf.duration;
  };

  // Socket'ga tinglovchilar faqat birinchi 🔊 bosilganda ulanadi (socket qayta yaratilsa — yangisiga).
  const bind = (sock) => {
    if (!sock || r.current.sock === sock) return;
    r.current.sock = sock;
    sock.on('emulator:audio', (p) => { if (mine(p) && r.current.on[p.slot || 0]) play(p.slot || 0, p.data); });
    sock.on('emulator:audio_end', (p) => {
      if (!mine(p)) return;
      const slot = p.slot || 0;
      if (!r.current.on[slot]) return;
      delete r.current.on[slot];
      set(slot, null);
      if (notifyRef.current) notifyRef.current(`🔇 E${slot + 1}: ${p.reason || "ovoz to'xtadi"}`);
    });
    // Qayta ulanishda server obunani unutgan — yoqilgan slotlarni qayta so'raymiz.
    sock.on('connect', () => {
      Object.keys(r.current.on).forEach(sl => sock.emit('emulator:audio_start', { slot: +sl, asUser: asUserRef.current }, (res) => {
        if (!res || !res.ok) { delete r.current.on[sl]; set(+sl, null); }
      }));
    });
  };

  const start = (slot) => {
    const sock = socketRef.current;
    if (!sock) return;
    if (!r.current.ctx) {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) { if (notifyRef.current) notifyRef.current("Bu brauzer ovoz ijro eta olmaydi"); return; }
      const ctx = new AC();
      // Daraja AGC bilan tekislanadi (play); cheklagich keskin boshlangan tovush cho'qqisini qirqilishdan saqlaydi.
      const gain = ctx.createGain();
      gain.gain.value = 1;
      const lim = ctx.createDynamicsCompressor();
      lim.threshold.value = -3; lim.knee.value = 0; lim.ratio.value = 20; lim.attack.value = 0.003; lim.release.value = 0.25;
      gain.connect(lim);
      lim.connect(ctx.destination);
      r.current.ctx = ctx;
      r.current.out = gain;
    }
    try { r.current.ctx.resume(); } catch (e) {}
    bind(sock);
    r.current.on[slot] = true;
    r.current.at[slot] = 0;
    delete r.current.agc[slot];
    set(slot, 'starting');
    // Server javob bermasa (eski backend / uzilish) ⏳ da qotib qolmasin.
    const t = setTimeout(() => {
      if (!r.current.on[slot] || r.current.on[slot] === 'ok') return;
      delete r.current.on[slot];
      set(slot, null);
      if (notifyRef.current) notifyRef.current(`🔇 E${slot + 1}: server javob bermadi — keyinroq qayta urinib ko'ring`);
    }, 60000);
    sock.emit('emulator:audio_start', { slot, asUser: asUserRef.current }, (res) => {
      clearTimeout(t);
      if (!r.current.on[slot]) return;
      if (res && res.ok) { r.current.on[slot] = 'ok'; set(slot, 'on'); return; }
      delete r.current.on[slot];
      set(slot, null);
      if (notifyRef.current) notifyRef.current(`🔇 E${slot + 1}: ${(res && res.error) || 'ovozni yoqib bo\'lmadi'}`);
    });
  };

  const stop = (slot) => {
    delete r.current.on[slot];
    set(slot, null);
    const sock = socketRef.current;
    if (sock) sock.emit('emulator:audio_stop', { slot, asUser: asUserRef.current });
  };

  const toggleAudio = (slot) => { slot = +slot || 0; if (r.current.on[slot]) stop(slot); else start(slot); };

  // Oyna yopilsa/komponent ketsa — server ushlagichini bo'shatamiz (uzilish ham o'zi bo'shatadi).
  useEffect(() => () => {
    const sock = r.current.sock;
    Object.keys(r.current.on).forEach(sl => { try { sock && sock.emit('emulator:audio_stop', { slot: +sl, asUser: asUserRef.current }); } catch (e) {} });
    r.current.on = {};
    try { if (r.current.ctx) r.current.ctx.close(); } catch (e) {}
  }, []);

  return { audio, toggleAudio };
}

// Tugma: 🔇 (o'chiq) → ⏳ (ulanmoqda) → 🔊 (eshitilmoqda). className — joyiga mos asosiy uslub (o'lcham/burchak),
// offCls/onCls — o'chiq va yoqilgan holat ranglari (Tailwind'da ikkita bg-* klassi to'qnashmasin deb alohida).
export function EmuAudioBtn({ slot, audio, onToggle, className = '', offCls = '', onCls = 'bg-emerald-600 hover:bg-emerald-700 text-white' }) {
  const st = audio && audio[slot];
  return (
    <button type="button" onClick={() => onToggle(slot)}
      title={st ? "Emulator ovozi eshitilmoqda — bosib o'chiring" : "Emulator ovozini shu yerda eshitish (bildirishnoma, qo'ng'iroq ohangi, video, musiqa)"}
      className={`${className} ${st ? onCls : offCls}`}>
      {st === 'starting' ? '⏳' : st ? '🔊' : '🔇'}
    </button>
  );
}
